import { DestroyRef, Injectable, signal, computed, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, switchMap, catchError, of, debounceTime } from 'rxjs';
import type { PaymentGroup, PaginationInfo, PaymentListItem, AppliedFilters } from '../models';
import { TransactionService, NotificationService } from '../services';
import { extractErrorMessage } from '@shared/utils/http-error.util';
import { mapPaymentToListItem } from '@shared/utils/payment-mapper.util';
import { sortByField } from '@shared/utils/filter-sort.util';
import { TransactionColumnStore } from './transaction-column.store';
import { TransactionDetailStore } from './transaction-detail.store';

/**
 * Default pagination configuration
 */
const DEFAULT_PAGINATION: PaginationInfo = {
  current_page: 1,
  total_pages: 0,
  total_records: 0,
  per_page: 20,
  has_next: false,
  has_previous: false,
};

/**
 * Debounce time for load requests (milliseconds)
 * Prevents rapid-fire API calls when user changes filters or pagination.
 * switchMap ensures old requests are cancelled.
 */
const LOAD_DEBOUNCE_TIME = 100;

/**
 * Transaction Store
 * Centralized state management for the transaction list using Angular Signals
 * and RxJS.
 *
 * @description
 * This store is the component-facing facade for the transactions feature. It
 * owns the list-loading pipeline, row selection, and sort state, and composes
 * two focused sub-stores whose public surface it re-exposes unchanged:
 *  - {@link TransactionColumnStore} — dynamic column config + visibility
 *  - {@link TransactionDetailStore}  — transaction-detail cache + loading
 *
 * The reactive architecture provides signal-based state, automatic request
 * cancellation via switchMap, memory-leak prevention via DestroyRef, debounced
 * loading, and centralized error handling with toast notifications.
 */
@Injectable({
  providedIn: 'root',
})
export class TransactionStore {
  private readonly transactionService = inject(TransactionService);
  private readonly notificationService = inject(NotificationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly columnStore = inject(TransactionColumnStore);
  private readonly detailStore = inject(TransactionDetailStore);

  private readonly loadRequest$ = new Subject<{
    page: number;
    perPage: number;
    filters?: AppliedFilters;
  }>();

  // State signals
  private readonly _paymentGroups = signal<PaymentGroup[]>([]);
  private readonly _loading = signal<boolean>(true); // Start as true to show skeleton on initial load
  private readonly _error = signal<string | null>(null);
  private readonly _pagination = signal<PaginationInfo>(DEFAULT_PAGINATION);
  private readonly _selectedIds = signal<Set<string>>(new Set());

  // Sort state signals
  private readonly _sortField = signal<string | null>(null);
  private readonly _sortDirection = signal<'asc' | 'desc'>('asc');

  constructor() {
    // Set up reactive loading pipeline with automatic cleanup
    // - debounceTime: Prevents API spam from rapid pagination clicks
    // - switchMap: Automatically cancels previous requests
    // - takeUntilDestroyed: Prevents memory leaks by auto-unsubscribing
    this.loadRequest$
      .pipe(
        debounceTime(LOAD_DEBOUNCE_TIME),
        switchMap(({ page, perPage, filters }) => {
          this._loading.set(true);
          this._error.set(null);
          return this.transactionService.loadTransactions(page, perPage, filters).pipe(
            catchError((error) => {
              // Show error toast with a safe, user-friendly message — never the
              // raw HttpErrorResponse text (which leaks the API URL/status).
              const message = extractErrorMessage(error, 'Échec du chargement des transactions');
              this.notificationService.error(message, 'Erreur de chargement');
              this._error.set(message);
              this._loading.set(false);
              return of({
                response: [],
                pagination: DEFAULT_PAGINATION,
              });
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (response) => {
          this._paymentGroups.set(response.response);
          this._pagination.set(response.pagination);
          this._loading.set(false);
        },
        error: (error) => {
          // Belt-and-suspenders: catchError should handle this, but add fallback
          console.error('Unhandled error in transaction store:', error);
          this._loading.set(false);
        },
      });
  }

  // ── Public list state ───────────────────────────────────────────────────────
  readonly paymentGroups = this._paymentGroups.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly pagination = this._pagination.asReadonly();
  readonly selectedIds = this._selectedIds.asReadonly();

  // ── Column state (delegated to TransactionColumnStore) ────────────────────────
  readonly columns = this.columnStore.columns;
  readonly columnsLoading = this.columnStore.columnsLoading;
  readonly columnsError = this.columnStore.columnsError;
  readonly columnVisibility = this.columnStore.columnVisibility;
  readonly columnWidths = this.columnStore.columnWidths;
  readonly visibleColumns = this.columnStore.visibleColumns;
  readonly sortableColumns = this.columnStore.sortableColumns;

  // ── Sort state ────────────────────────────────────────────────────────────────
  readonly sortField = this._sortField.asReadonly();
  readonly sortDirection = this._sortDirection.asReadonly();

  setSortField(field: string | null): void {
    this._sortField.set(field);
  }

  setSortDirection(dir: 'asc' | 'desc'): void {
    this._sortDirection.set(dir);
  }

  // ── Transaction detail (delegated to TransactionDetailStore) ──────────────────
  readonly detailLoading = this.detailStore.detailLoading;
  readonly detailError = this.detailStore.detailError;
  readonly currentTransactionDetail = this.detailStore.currentTransactionDetail;

  /**
   * Flat list of all payments (for table display), optionally sorted.
   *
   * Transforms grouped payment data into a flat array via the shared row mapper,
   * then sorts on the mapped {@link PaymentListItem} fields using the shared
   * comparator. Sorting on the mapped fields (not raw Payment fields) avoids
   * type casts and ensures correct comparisons for complex fields like `status`.
   */
  readonly flatPayments = computed<PaymentListItem[]>(() => {
    const field = this._sortField();
    const dir = this._sortDirection();
    const flat = this._paymentGroups().flatMap((group) =>
      group.payments.map((p) => mapPaymentToListItem(p)),
    );
    if (!field) return flat;
    return sortByField(flat, field, dir);
  });

  /** Selected payments (uses Set.has() for O(1) lookup). */
  readonly selectedPayments = computed<PaymentListItem[]>(() => {
    const selected = this._selectedIds();
    return this.flatPayments().filter((p) => selected.has(p.id));
  });

  /** True only if there are payments AND all are selected. */
  readonly allSelected = computed<boolean>(() => {
    const flat = this.flatPayments();
    const selected = this._selectedIds();
    return flat.length > 0 && flat.every((p) => selected.has(p.id));
  });

  /**
   * Load payment history with pagination and filters.
   * Debounced and auto-cancelling; clears selection when loading a new page.
   *
   * @param page    - Page number (1-indexed, must be >= 1)
   * @param perPage - Items per page (must be between 1 and 100)
   * @param filters - Optional filters to apply
   */
  loadPayments(page = 1, perPage = 20, filters?: AppliedFilters): void {
    if (page < 1) {
      console.warn(`Invalid page number: ${page}. Using page 1.`);
      page = 1;
    }

    if (perPage < 1 || perPage > 100) {
      console.warn(`Invalid perPage: ${perPage}. Using default 20.`);
      perPage = 20;
    }

    // Clear selections when loading new page to avoid confusion
    this.clearSelection();

    this.loadRequest$.next({ page, perPage, filters });
  }

  /**
   * Toggle payment selection. Creates a new Set for signal change detection.
   * @param id - Unique payment identifier
   */
  toggleSelection(id: string): void {
    if (!id) {
      console.warn('toggleSelection called with invalid id');
      return;
    }

    this._selectedIds.update((selected) => {
      const newSet = new Set(selected);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  }

  /** Select all payments on the current page. */
  selectAll(): void {
    const allIds = this.flatPayments().map((p) => p.id);
    this._selectedIds.set(new Set(allIds));
  }

  /** Deselect all payments. Called automatically when loading a new page. */
  clearSelection(): void {
    this._selectedIds.set(new Set());
  }

  /**
   * Replace the selection with a new set of ids.
   * Called when PrimeNG's [(selection)] two-way binding emits selectionChange.
   */
  setSelection(ids: string[]): void {
    this._selectedIds.set(new Set(ids));
  }

  /** Selects all if none/partial selected, deselects all if all selected. */
  toggleSelectAll(): void {
    if (this.allSelected()) {
      this.clearSelection();
    } else {
      this.selectAll();
    }
  }

  // ── Transaction detail delegation ─────────────────────────────────────────────

  /**
   * Load a transaction detail by id.
   *
   * Stale-while-revalidate: a cached response paints immediately, but the server
   * is always re-queried so a status that changed server-side shows up without a
   * browser refresh.
   *
   * @param transactionId - Transaction/payment id
   */
  loadTransactionDetail(transactionId: string): void {
    this.detailStore.loadTransactionDetail(transactionId);
  }

  /** Remove all cached transaction details. */
  clearTransactionDetailCache(): void {
    this.detailStore.clearTransactionDetailCache();
  }

  /** Evict a single cached transaction detail so the next load refetches. */
  invalidateTransactionDetail(transactionId: string): void {
    this.detailStore.invalidateTransactionDetail(transactionId);
  }

  /** Reset the current transaction id and clear related error state. */
  clearCurrentTransactionDetail(): void {
    this.detailStore.clearCurrentTransactionDetail();
  }

  // ── Column delegation ─────────────────────────────────────────────────────────

  /**
   * Load dynamic column definitions from the server.
   * @param forceRefresh - Force reload even if cached (default: false)
   */
  loadColumns(forceRefresh = false): void {
    this.columnStore.loadColumns(forceRefresh);
  }

  /**
   * Toggle a column's visibility.
   * @returns `true` if toggled, `false` if prevented (last visible column).
   */
  toggleColumnVisibility(columnKey: string): boolean {
    return this.columnStore.toggleColumnVisibility(columnKey);
  }

  /** Make all columns visible. */
  showAllColumns(): void {
    this.columnStore.showAllColumns();
  }

  /** Hide all columns except the first. */
  hideAllColumns(): void {
    this.columnStore.hideAllColumns();
  }

  /**
   * Reorder columns after a drag-and-drop within the visible columns array.
   * @param fromIdx - Drag origin index within the visible columns array
   * @param toIdx   - Drop target index within the visible columns array
   */
  reorderVisibleColumn(fromIdx: number, toIdx: number): void {
    this.columnStore.reorderVisibleColumn(fromIdx, toIdx);
  }

  /**
   * Persist a column's manually-resized width.
   * @param columnKey - Server column key (snake_case)
   * @param width     - New width in pixels
   */
  setColumnWidth(columnKey: string, width: number): void {
    this.columnStore.setColumnWidth(columnKey, width);
  }
}
